// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Encounter, Patient } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition, CancelAppointmentResult } from '@polarissleep/scheduling';
import {
  cancelAppointment as cancelLegacyAppointment,
  REPLACED_BY_APPOINTMENT_EXTENSION_URL,
} from '@polarissleep/scheduling';
import { isV2Appointment } from '@polarissleep/scheduling-next';
import * as Sentry from '@sentry/react';
import { createProviderSchedulingClient, getProviderSchedulingScope } from '../scheduling-next/client';
import type { ProviderAppointmentCandidate } from '../scheduling-next/types';
import { bookProviderV2Appointment } from './booking-service-v2';

export interface ProviderV2RescheduleResult {
  newAppointmentId: string;
  newEncounterId: string;
  oldAppointmentId: string;
}

export async function cancelProviderAppointment(
  medplum: MedplumClient,
  appointmentId: string,
  reason?: string | null
): Promise<CancelAppointmentResult> {
  const normalizedReason = reason ?? undefined;
  const appointment = await medplum.readResource('Appointment', appointmentId);
  if (isV2Appointment(appointment) && appointment.status === 'fulfilled') {
    throw new Error(`Cannot cancel fulfilled Appointment/${appointmentId}`);
  }
  if (appointment.status === 'cancelled') {
    return { appointmentId, slotReleased: false, slotsRestored: 0, encounterCancelled: false };
  }
  if (!isV2Appointment(appointment)) {
    return cancelLegacyAppointment(medplum, appointmentId, normalizedReason);
  }

  await createProviderSchedulingClient(medplum).cancel({
    appointmentId,
    scope: getProviderSchedulingScope(),
    ...(normalizedReason ? { cancelationReason: { text: normalizedReason } } : {}),
  });
  const encounters = await medplum.searchResources('Encounter', {
    appointment: `Appointment/${appointmentId}`,
    _count: '1',
  });
  const encounter = encounters[0] as Encounter | undefined;
  if (encounter?.id && encounter.status !== 'cancelled' && encounter.status !== 'finished') {
    try {
      await medplum.updateResource<Encounter>({ ...encounter, status: 'cancelled' });
    } catch (error) {
      Sentry.captureException(error, { extra: { appointmentId, operation: 'provider-v2-cancel-encounter' } });
    }
  }

  return { appointmentId, slotReleased: false, slotsRestored: 0, encounterCancelled: Boolean(encounter?.id) };
}

export async function rescheduleProviderV2Appointment(
  medplum: MedplumClient,
  params: {
    appointmentId: string;
    patient: Patient;
    appointmentType: AppointmentTypeDefinition;
    candidate: ProviderAppointmentCandidate;
  }
): Promise<ProviderV2RescheduleResult> {
  const oldAppointment = await medplum.readResource('Appointment', params.appointmentId);
  if (oldAppointment.status !== 'booked') {
    throw new Error('Only booked appointments can be rescheduled.');
  }

  const booked = await bookProviderV2Appointment(medplum, {
    ...params,
    replacesAppointmentId: params.appointmentId,
  });

  try {
    await medplum.updateResource<Appointment>({
      ...oldAppointment,
      extension: [
        ...(oldAppointment.extension ?? []).filter(
          (extension) => extension.url !== REPLACED_BY_APPOINTMENT_EXTENSION_URL
        ),
        {
          url: REPLACED_BY_APPOINTMENT_EXTENSION_URL,
          valueReference: { reference: `Appointment/${booked.appointmentId}` },
        },
      ],
    });
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        appointmentId: params.appointmentId,
        newAppointmentId: booked.appointmentId,
        operation: 'link-replacement',
      },
    });
  }

  try {
    await cancelProviderAppointment(medplum, params.appointmentId, 'Rescheduled');
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        appointmentId: params.appointmentId,
        newAppointmentId: booked.appointmentId,
        operation: 'v2-cancel-old',
      },
    });
  }

  return {
    newAppointmentId: booked.appointmentId,
    newEncounterId: booked.encounterId,
    oldAppointmentId: params.appointmentId,
  };
}
